/**
 * Cross-tab session invalidation channel (TA-2593, VAPT).
 *
 * The backend issues stateless JWTs and exposes no revoke endpoint, so a
 * session only ends when the client drops its token. Without a broadcast,
 * a browser with several TaskTag tabs open keeps every *other* tab
 * authenticated after one tab logs out — or is logged out by the errorLink
 * 401 path following a password reset — until that tab happens to reload.
 *
 * Effectively web-only: `BroadcastChannel` is not part of the React Native
 * runtime, so on native every entry point below no-ops via the feature check
 * in `getChannel()`. Native also has one session per app install, so there is
 * nothing to synchronise there.
 */
const CHANNEL_NAME = 'tasktag-session';
const SESSION_ENDED = 'session-ended';

// `undefined` = not resolved yet, `null` = unavailable in this environment.
let channel: BroadcastChannel | null | undefined;

const getChannel = (): BroadcastChannel | null => {
  if (channel !== undefined) return channel;

  if (typeof BroadcastChannel === 'undefined') {
    channel = null;
    return channel;
  }

  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    // Node's BroadcastChannel (the one Jest runs on) is a ref'd libuv handle,
    // so an open channel would keep the test process alive after the last
    // suite. Browsers have no `unref`, hence the optional call.
    (channel as { unref?: () => void }).unref?.();
  } catch {
    // Private-browsing / policy-blocked environments: degrade to a no-op
    // rather than taking the whole auth context down.
    channel = null;
  }
  return channel;
};

/** Tell every other tab in this browser that the session has ended. */
export const broadcastSessionEnded = (): void => {
  try {
    getChannel()?.postMessage(SESSION_ENDED);
  } catch {
    // A failed broadcast must never block the local logout that triggered it.
  }
};

/**
 * Run `handler` when another tab reports that the session ended. Returns an
 * unsubscribe function — a no-op where `BroadcastChannel` is unavailable.
 */
export const subscribeToSessionEnded = (handler: () => void): (() => void) => {
  const activeChannel = getChannel();
  if (!activeChannel) return () => undefined;

  const listener = (event: MessageEvent) => {
    if (event?.data === SESSION_ENDED) handler();
  };

  activeChannel.addEventListener('message', listener);
  return () => activeChannel.removeEventListener('message', listener);
};
